/**
 * OV desktop gallery navigation
 * Adds to the product media gallery on desktop (mouse/trackpad) only:
 *   1. Click-and-drag the main photo to move to the next/previous photo
 *   2. Click-and-drag the thumbnail strip
 *   3. Mouse wheel scrolls the thumbnail strip sideways
 *   4. Left/right arrows on the sides of the main photo (shown on hover)
 *   5. Keyboard left/right arrows move between photos
 * Touch devices are untouched. Works alongside media-gallery.js and the
 * "show media only for the selected variant" setting (hidden photos are skipped).
 */
(() => {
  if (window.ovGalleryDesktopLoaded) return;
  window.ovGalleryDesktopLoaded = true;

  const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');
  const DRAG_THRESHOLD = 6; // px before a press counts as a drag
  const SWIPE_DISTANCE = 40; // px of drag needed to change photo

  const chevron = (dir) => `<svg width="24" height="24" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor" aria-hidden="true" focusable="false" class="icon"><path d="${dir === 'next' ? 'm8 4.7 8 8-8 6.6' : 'm16 4.7-8 8 8 6.6'}" fill="none"/></svg>`;

  /** Visible (not hidden by variant grouping) main photos, in display order. */
  const visibleItems = (viewer) => Array.from(viewer.querySelectorAll('.media-viewer__item'))
    .filter((el) => !el.hidden && el.offsetWidth > 0);

  /** Index of the visible photo closest to the viewer's current scroll position. */
  const currentIndex = (viewer, items) => {
    let best = 0;
    let bestDist = Infinity;
    items.forEach((el, i) => {
      const dist = Math.abs(el.offsetLeft - viewer.scrollLeft);
      if (dist < bestDist) { best = i; bestDist = dist; }
    });
    return best;
  };

  const goTo = (viewer, index, smooth = true) => {
    const items = visibleItems(viewer);
    if (!items.length) return;
    const i = Math.max(0, Math.min(items.length - 1, index));
    viewer.scrollTo({ left: items[i].offsetLeft, behavior: smooth ? 'smooth' : 'auto' });
  };

  const step = (viewer, delta) => {
    const items = visibleItems(viewer);
    goTo(viewer, currentIndex(viewer, items) + delta);
  };

  /** Swallow the click that follows a drag so it doesn't open zoom / pick a thumbnail. */
  const suppressNextClick = (el) => {
    const handler = (e) => {
      e.preventDefault();
      e.stopPropagation();
      el.removeEventListener('click', handler, true);
    };
    el.addEventListener('click', handler, true);
    setTimeout(() => el.removeEventListener('click', handler, true), 400);
  };

  /** Generic mouse drag-to-scroll for a horizontal scroller. */
  const enableDrag = (scroller, { onRelease } = {}) => {
    let startX = 0;
    let startScroll = 0;
    let pressed = false;
    let dragging = false;
    let pointerId = null;

    scroller.addEventListener('pointerdown', (e) => {
      if (e.pointerType !== 'mouse' || e.button !== 0) return;
      if (scroller.scrollWidth <= scroller.clientWidth + 1) return;
      pressed = true;
      dragging = false;
      startX = e.clientX;
      startScroll = scroller.scrollLeft;
      pointerId = e.pointerId;
    });

    scroller.addEventListener('pointermove', (e) => {
      if (!pressed || e.pointerId !== pointerId) return;
      const dx = e.clientX - startX;
      if (!dragging && Math.abs(dx) > DRAG_THRESHOLD) {
        dragging = true;
        scroller.classList.add('ov-is-dragging');
        try { scroller.setPointerCapture(pointerId); } catch (err) { /* ignore */ }
      }
      if (dragging) {
        scroller.scrollLeft = startScroll - dx;
        e.preventDefault();
      }
    });

    const end = (e) => {
      if (!pressed || (e && e.pointerId !== pointerId)) return;
      pressed = false;
      if (!dragging) return;
      dragging = false;
      try { scroller.releasePointerCapture(pointerId); } catch (err) { /* ignore */ }
      suppressNextClick(scroller);
      const dx = (e ? e.clientX : startX) - startX;
      if (onRelease) {
        onRelease(dx, startScroll);
        // keep snapping off until the smooth scroll has settled
        setTimeout(() => scroller.classList.remove('ov-is-dragging'), 450);
      } else {
        scroller.classList.remove('ov-is-dragging');
      }
    };

    scroller.addEventListener('pointerup', end);
    scroller.addEventListener('pointercancel', end);
    scroller.addEventListener('lostpointercapture', () => {
      if (dragging) end();
    });
    // Stop the browser's native image drag ("ghost image")
    scroller.addEventListener('dragstart', (e) => e.preventDefault());
  };

  const initGallery = (gallery) => {
    if (gallery.dataset.ovDesktop) return;
    if (gallery.dataset.layout === 'stacked') return; // grid layout needs none of this
    const viewer = gallery.querySelector('.media-viewer');
    const viewerWrap = gallery.querySelector('.media-gallery__viewer');
    if (!viewer || !viewerWrap) return;
    gallery.dataset.ovDesktop = 'true';
    gallery.classList.add('ov-desktop-gallery');

    // 1. Drag the main photo: snap to next/previous photo on release
    enableDrag(viewer, {
      onRelease: (dx, startScroll) => {
        const items = visibleItems(viewer);
        let startIndex = 0;
        let bestDist = Infinity;
        items.forEach((el, i) => {
          const dist = Math.abs(el.offsetLeft - startScroll);
          if (dist < bestDist) { startIndex = i; bestDist = dist; }
        });
        let target = startIndex;
        if (dx <= -SWIPE_DISTANCE) target = startIndex + 1;
        else if (dx >= SWIPE_DISTANCE) target = startIndex - 1;
        goTo(viewer, target);
      },
    });

    // 2 + 3. Thumbnail strip: drag and mouse wheel
    const thumbs = gallery.querySelector('.media-thumbs');
    if (thumbs) {
      enableDrag(thumbs);
      thumbs.addEventListener('wheel', (e) => {
        if (thumbs.scrollWidth <= thumbs.clientWidth + 1) return;
        if (Math.abs(e.deltaX) > Math.abs(e.deltaY)) return; // trackpad sideways swipe: let the browser handle it
        const unit = e.deltaMode === 1 ? 16 : (e.deltaMode === 2 ? thumbs.clientWidth : 1);
        const delta = e.deltaY * unit;
        const atStart = thumbs.scrollLeft <= 0;
        const atEnd = thumbs.scrollLeft + thumbs.clientWidth >= thumbs.scrollWidth - 1;
        // At either end, let the page scroll normally
        if ((delta < 0 && atStart) || (delta > 0 && atEnd)) return;
        thumbs.scrollLeft += delta;
        e.preventDefault();
      }, { passive: false });
    }

    // 4. Side arrows on the main photo
    const prev = document.createElement('button');
    const next = document.createElement('button');
    [[prev, 'prev', 'Previous photo'], [next, 'next', 'Next photo']].forEach(([btn, dir, label]) => {
      btn.type = 'button';
      btn.className = `ov-gallery-arrow ov-gallery-arrow--${dir}`;
      btn.setAttribute('aria-label', label);
      btn.setAttribute('aria-controls', viewer.id || 'media-viewer');
      btn.innerHTML = chevron(dir);
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        step(viewer, dir === 'next' ? 1 : -1);
      });
      viewerWrap.appendChild(btn);
    });

    const updateArrows = () => {
      const items = visibleItems(viewer);
      const show = items.length > 1;
      prev.hidden = !show;
      next.hidden = !show;
      if (!show) return;
      const i = currentIndex(viewer, items);
      prev.disabled = i <= 0;
      next.disabled = i >= items.length - 1;
    };
    let rafId = null;
    viewer.addEventListener('scroll', () => {
      if (rafId) return;
      rafId = requestAnimationFrame(() => { rafId = null; updateArrows(); });
    }, { passive: true });
    viewerWrap.addEventListener('pointerenter', updateArrows);
    const section = gallery.closest('.js-product');
    if (section) section.addEventListener('on:variant:change', () => setTimeout(updateArrows, 100));
    window.addEventListener('on:debounced-resize', updateArrows);
    setTimeout(updateArrows, 300);

    // 5. Keyboard arrows while the gallery has focus
    gallery.addEventListener('keydown', (e) => {
      if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
      if (e.target.closest('input, textarea, select, [contenteditable]')) return;
      e.preventDefault();
      step(viewer, e.key === 'ArrowRight' ? 1 : -1);
    });
  };

  const initAll = () => {
    if (!finePointer.matches) return;
    document.querySelectorAll('media-gallery').forEach((g) => {
      // wait for the theme's own gallery script to finish setting up
      if (g.hasAttribute('loaded')) initGallery(g);
      else setTimeout(() => initGallery(g), 300);
    });
  };

  const start = () => {
    customElements.whenDefined('media-gallery').then(initAll);
    // Galleries added later (quick view, theme editor) get set up too
    new MutationObserver((mutations) => {
      if (!finePointer.matches) return;
      if (mutations.some((m) => Array.from(m.addedNodes).some((n) => n.nodeType === 1
        && (n.matches('media-gallery') || n.querySelector('media-gallery'))))) {
        setTimeout(initAll, 300);
      }
    }).observe(document.body, { childList: true, subtree: true });
    finePointer.addEventListener?.('change', initAll);
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})();
